import { parseLeadAgentDashboardAudience, type tLeadAgentDashboardAudience } from './leadAgentDashboard';

// ENV
export const VITE_ENV: string = import.meta?.env?.VITE_ENV ?? 'local';
export const IS_PRODUCTION: boolean = VITE_ENV === 'production';
export const IS_PRODUCTION_OR_STAGING: boolean =
  VITE_ENV === 'production' || VITE_ENV === 'staging';
/**
 * Use for DEV-only UI surfaces (DevToolbar, simulators, escape hatches).
 * Driven by the explicit `VITE_ENV` variable rather than `import.meta.env.DEV`
 * — Vite derives the latter from `mode !== 'production'`, so a stray
 * `vite build --mode=development` against a deployed entrypoint would leak
 * DEV surfaces. Keying off `VITE_ENV === 'local'` keeps the contract pinned
 * to environment identity, not build mode.
 */
export const IS_LOCAL_DEV: boolean = VITE_ENV === 'local';
/**
 * Shows the WhatsApp API connection wizard's screen simulator. On in local
 * development, and in a deployed build only when its own config sets
 * `VITE_WAPI_WIZARD_SIMULATOR=true` (a PR preview), so staging and production
 * never render it.
 */
export const IS_WAPI_WIZARD_SIMULATOR_ENABLED: boolean =
  IS_LOCAL_DEV || import.meta.env?.VITE_WAPI_WIZARD_SIMULATOR === 'true';

// Facebook
export const VITE_FB_CONFIG_ID = import.meta.env?.VITE_FB_CONFIG_ID;
export const VITE_WAB_CONFIG_ID = import.meta.env?.VITE_WAB_CONFIG_ID;
export const VITE_FB_CALLBACK_URL = import.meta.env?.VITE_FB_CALLBACK_URL;
export const VITE_META_FEATURE = import.meta.env?.VITE_META_FEATURE;

// Google Maps
export const GOOGLE_MAPS_API_KEY = import.meta.env?.VITE_API_KEY;

// Whatsapp Business
export const VITE_WA_BUSINESS_WIDS = import.meta.env?.VITE_WA_BUSINESS_WIDS;

// Intercom
export const VITE_INTERCOM_APP_ID = import.meta.env?.VITE_INTERCOM_APP_ID;

// Birdie
export const VITE_BIRDIE_API_KEY = import.meta.env?.VITE_BIRDIE_API_KEY;

// TikTok
export const VITE_TIKTOK_FEATURE = import.meta.env?.VITE_TIKTOK_FEATURE === 'true';
export const VITE_TIKTOK_ALLOWED_WORKSPACES: readonly string[] =
  (import.meta.env?.VITE_TIKTOK_ALLOWED_WORKSPACES ?? '')
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

// Knowledge Files
export const VITE_MAX_KNOWLEDGE_FILE_SIZE: number =
  Number(import.meta.env?.VITE_MAX_KNOWLEDGE_FILE_SIZE) || 30 * 1024 * 1024;

/**
 * Numeric env with a default, tolerating unset/blank/garbage.
 *
 * Deliberately not the `Number(...) || fallback` idiom used above: that treats
 * a legitimate `0` as "unset" and silently restores the default, which would
 * make the availability-prompt kill switch below impossible to set.
 *
 * Blank is "unset", not zero, and that distinction is the whole point of the
 * explicit emptiness check: `VITE_X=` with no value is an ordinary deploy
 * state, and `Number('') === 0` would read it as an intentional zero — for the
 * kill switch below, silently disabling the feature nobody meant to disable.
 * Negative and non-numeric values fall back for the same reason: a typo should
 * degrade to the documented default, never to a surprising behaviour.
 */
export function envNumber(raw: unknown, fallback: number): number {
  if (raw === undefined || raw === null) return fallback;
  if (typeof raw === 'string' && raw.trim() === '') return fallback;

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

// BUG-1116: all new documents get the boot hold; 0 remains the kill switch.
const authBootShareDefault = 1;
const authBootShare = envNumber(import.meta.env?.VITE_AUTH_BOOT_HOLD_SHARE, authBootShareDefault);
export const VITE_AUTH_BOOT_HOLD_SHARE = authBootShare <= 1 ? authBootShare : authBootShareDefault;

// Every wait uses this one budget from the first token request, including
// getIdToken and retry pauses. Bounds: the legacy 3 s wait to a 30 s ceiling.
// If Firebase never settles, flags give up around 28 s rather than 11 s;
// /onboarding remains blank meanwhile.
const authBootDeadline = envNumber(import.meta.env?.VITE_AUTH_BOOT_HOLD_DEADLINE_MS, 20_000);
export const VITE_AUTH_BOOT_HOLD_DEADLINE_MS =
  authBootDeadline >= 3_000 && authBootDeadline <= 30_000 ? authBootDeadline : 20_000;

/**
 * ISO-8601 instant env with a default, same contract as `envNumber` above —
 * unset/blank/garbage all degrade to the documented default, never to "no
 * cutover" (an absent instant treated as "always past" or "always future")
 * nor to the epoch (an unparseable string treated as `0`).
 *
 * The failure mode this guards against is the same one `envNumber` guards
 * against, just for a date instead of a quantity: a typo in the deployed
 * value must not silently disable or fast-forward a date-gated behaviour,
 * it must fall back to the value the code already documents as correct.
 * `Date.parse` returning `NaN` is this function's equivalent of `envNumber`'s
 * `parsed < 0` — the signal that the raw value isn't usable.
 *
 * `Date.parse` ALONE NO ALCANZA, y por eso hay una forma que validar antes
 * (CRisPR sobre RET-2234). Es permisivo de un modo que aquí es caro:
 *
 *   `'0'`   -> 2000-01-01   `'1'` -> 2001-01-01   `'2026'` -> 2026-01-01
 *
 * Un typo de un carácter en el despliegue mueve el corte de facturación años
 * al pasado, y un corte en el pasado significa **cobrar ya**. Es justo el
 * fallo silencioso hacia el lado caro que esta función existe para impedir:
 * `Date.parse` no devuelve `NaN`, así que la versión anterior lo aceptaba.
 *
 * Y hay un segundo filo, más sutil, que también se rechaza: una marca sin
 * designador de zona (`'2026-10-01T00:00:00'`) o en formato local
 * (`'10/01/2026'`, `'Oct 1 2026'`) la parsea JS **en la hora del runner**.
 * En CDMX eso corre el corte 6 h — exactamente el desfase que el default en
 * UTC se eligió para evitar, reintroducido por la puerta de atrás.
 *
 * Se acepta solo ISO-8601 inequívoco:
 *   - `YYYY-MM-DD` (la spec lo fija en UTC), o
 *   - `YYYY-MM-DDTHH:mm[:ss[.sss]]` con `Z` u offset explícito `±HH:mm`.
 * Todo lo demás cae al default documentado. `Date.parse` se sigue
 * consultando después, para atrapar lo que tiene forma válida pero no existe
 * (`2026-13-45`).
 */
const ISO_8601_INSTANT =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2}))?$/;

export function envDate(raw: unknown, fallback: string): string {
  if (raw === undefined || raw === null) return fallback;

  const value = String(raw).trim();
  if (value === '') return fallback;
  if (!ISO_8601_INSTANT.test(value)) return fallback;

  return Number.isNaN(Date.parse(value)) ? fallback : value;
}

// Availability prompt (RET-1350) — how hard we nudge a seller who is Inactivo
// but still working. Tunable without a code change because the right cadence
// is a product judgement: too few and a forgotten Inactivo costs a day of
// leads, too many and the prompt becomes noise people learn to dismiss.
// Set MAX_NUDGES=0 to disable the prompt entirely (kill switch).
export const VITE_AVAILABILITY_PROMPT_MAX_NUDGES: number = envNumber(
  import.meta.env?.VITE_AVAILABILITY_PROMPT_MAX_NUDGES,
  3,
);
/** Quiet window between nudges, so a burst of sends cannot spend them all at once. */
export const VITE_AVAILABILITY_PROMPT_COOLDOWN_MS: number =
  envNumber(import.meta.env?.VITE_AVAILABILITY_PROMPT_COOLDOWN_MINUTES, 30) *
  60_000;

// Chat albums (INT-1227): photos sent together render as one grid, the way
// WhatsApp Web shows an album. No message carries an album key, so two of the
// groupings are timing rules and their windows are tunable without a code change.
// - Inbound: the next photo of a WhatsApp API contact joins the album when its
//   timestamp is this close to the previous one. Meta stamps whole seconds, and a
//   real album spans 0-1 s between photos.
// - Outbound: the same, for photos one person sends from the Leadsales composer.
//   The composer uploads every file before sending it, so on a slow connection the
//   photos land seconds apart.
export const VITE_CHAT_ALBUM_INBOUND_GAP_SECONDS: number = envNumber(
  import.meta.env?.VITE_CHAT_ALBUM_INBOUND_GAP_SECONDS,
  2,
);
export const VITE_CHAT_ALBUM_OUTBOUND_GAP_SECONDS: number = envNumber(
  import.meta.env?.VITE_CHAT_ALBUM_OUTBOUND_GAP_SECONDS,
  10,
);
// WAPI Prototype (dashboard module — gated until production-ready)
export const VITE_WAPI_PROTOTYPE_FEATURE = import.meta.env?.VITE_WAPI_PROTOTYPE_FEATURE === 'true';

// Lead Agent Flows — per-workspace allowlist for the Flows config section.
// FAIL-CLOSED, unlike VITE_TIKTOK_ALLOWED_WORKSPACES: an empty list means NO
// workspace sees the section, not "every workspace". Sending a WhatsApp Flow
// needs Meta business verification per account, so this ships off for everyone
// and is opened one WID at a time. Emptying the list is also the kill switch,
// which is why there is no separate boolean flag.
export const VITE_LEADBOT_FLOWS_ALLOWED_WORKSPACES: readonly string[] =
  (import.meta.env?.VITE_LEADBOT_FLOWS_ALLOWED_WORKSPACES ?? '')
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

// WAPI AI templates (INT-1396) — per-workspace allowlist for the "Crear
// plantillas con IA" button in the Plantillas tab.
//
// FAIL-CLOSED, same contract as VITE_LEADBOT_FLOWS_ALLOWED_WORKSPACES: an empty
// list means NO workspace sees the button, and emptying it again is the kill
// switch, which is why there is no separate boolean flag. A `*` entry opens it
// to every workspace for the general rollout (see AI_TEMPLATES_ALL_WORKSPACES).
//
// Why a front-side gate at all, when the backend already has a cohort flag: the
// eligibility endpoint does not report it. Its six reasons cover the workspace's
// own data, not whether generation is armed for that cohort, so it answers
// `eligible` even where AI_TEMPLATE_GENERATION_ENABLED is off — and the button
// would show, spend a click, and end in a batch that declines.
export const VITE_WAPI_AI_TEMPLATES_ALLOWED_WORKSPACES: readonly string[] =
  (import.meta.env?.VITE_WAPI_AI_TEMPLATES_ALLOWED_WORKSPACES ?? '')
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

/**
 * Escalera de mora (RET-1269) — la superficie nueva de cobranza: contador
 * anclado al `daysPastDue` que publica el BFF, avisos permanentes y rampa de
 * color, en lugar de los tres modales de mora y su umbral local.
 *
 * APAGADO al mergear, y cubre la unidad que re-ancla el contador más las cinco
 * que la siguen (copia honesta, retiro de los modales, snapshot de alertas,
 * escalera de owner/admin, modal de detalle de pago). Se enciende cuando las
 * seis estén dentro: encenderlo antes dejaría al cliente con la escalera nueva
 * Y los modales viejos al mismo tiempo.
 */
export const VITE_PAST_DUE_LADDER_FEATURE =
  import.meta.env?.VITE_PAST_DUE_LADDER_FEATURE === 'true';

// Sandbox config-repair / feedback (gated until the feedback backend is in prod)
export const VITE_SANDBOX_CONFIG_REPAIR_FEATURE =
  import.meta.env?.VITE_SANDBOX_CONFIG_REPAIR_FEATURE === 'true';

// Sandbox async chat (202 + websocket push, polling fallback) — gated until
// chatbot-service/bff-web/websocket-service ship the async job contract.
export const VITE_SANDBOX_ASYNC_FEATURE =
  import.meta.env?.VITE_SANDBOX_ASYNC_FEATURE === 'true';

/**
 * Mensajes gratis en el saldo (RET-2077) — el panel del cupo de Meta y su detalle.
 *
 * ESTÁ APAGADO A PROPÓSITO HASTA EL 1-OCT-2026, y no por prudencia genérica: hoy
 * Meta marca TODOS los mensajes de servicio como cubiertos por el cupo, porque
 * todavía no cobra ninguno. El contador leería decenas de miles contra un tope de
 * 1,000, y aunque la vista lo topa para no mostrar un número imposible, un cliente
 * vería "1,000 de 1,000 · se acabó tu cupo" un mes antes de que el cupo exista.
 *
 * El 1 de octubre Meta empieza a marcar como cobrables los que pasan de mil y el
 * contador pasa a decir la verdad solo. Ese día se enciende esto.
 *
 * Fail-closed como el resto de las banderas de este archivo: cualquier valor que no
 * sea exactamente 'true' deja el panel apagado. Un typo en un despliegue no puede
 * encenderlo antes de tiempo.
 */
export const VITE_FREE_MESSAGES_COUNTER_FEATURE =
  import.meta.env?.VITE_FREE_MESSAGES_COUNTER_FEATURE === 'true';

/**
 * Corte de "UTILITY en ventana de 24h es gratis" (RET-2234, UC-09) —
 * `isTemplateFreeAtSource` en `useWalletSendGate.ts` deja de tratar una
 * UTILITY dentro de la ventana activa como gratis en origen a partir de este
 * instante (free entry point NO se toca: sigue gratis a los dos lados).
 *
 * DEFAULT `2026-10-01T00:00:00.000Z`: medianoche UTC, no medianoche local. La
 * base es LATAM, toda detrás de UTC, así que UTC llega antes que la hora
 * local — el volteo a "Con costo" ocurre hasta 6h temprano, que es el lado
 * seguro (promete de menos); anclar a medianoche local prometería de más
 * hasta 6h el día de la frontera.
 *
 * Overridable por la misma razón que el resto de este archivo permite mover
 * fechas de negocio: Meta ya pospuso este corte antes, y mover el override al
 * pasado en staging deja recorrer todo el árbol post-corte antes del día real.
 *
 * `envDate`, no `envNumber`: incluido aquí solo por paralelismo de contrato.
 * Un valor ausente, en blanco o que `Date.parse` no entienda cae al DEFAULT
 * de arriba, nunca a "no hay corte" (el lado inseguro) ni a la época cero.
 */
/**
 * ÚNICO domicilio del literal de corte (RET-2234, checker arquitectónico de
 * la vuelta 2): antes vivía DOS veces — aquí como default inline de `envDate`
 * y, sin exportarse, copiado a mano en `useWalletSendGate.ts` como respaldo
 * contra un mock parcial de este módulo. El único amarre entre las dos copias
 * era un comentario ("Debe coincidir con el DEFAULT") — y la tesis de este
 * ticket es justo que un comentario no falla cuando el código diverge.
 *
 * Exportada para que ese respaldo IMPORTE este valor en vez de repetirlo: un
 * mock parcial (`importOriginal` + override de solo
 * `VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL`) preserva el resto del módulo, así
 * que esta constante sigue disponible ahí sin necesitar una segunda copia.
 */
export const UTILITY_WINDOW_CUTOVER_DEFAULT = '2026-10-01T00:00:00.000Z';

/**
 * El override sólo puede mover el corte HACIA ADELANTE en producción.
 *
 * El override existe para viajar en el tiempo en staging: ponerlo en el pasado
 * deja recorrer el árbol post-corte antes del día real. Esa misma libertad en
 * un build de producción es un cobro adelantado — el front empieza a decir
 * «Con costo» y a exigir saldo antes de que Meta cobre nada—, y hasta ahora lo
 * único que lo impedía era prosa en un README.
 *
 * Adelantarlo es el único movimiento peligroso, así que es el único que se
 * bloquea: retrasarlo sigue permitido sin fricción, que es lo que hace falta
 * el día que Meta vuelva a posponer el corte —ya lo hizo una vez— y haya que
 * responder con un redeploy, no con un PR.
 *
 * Cae al DEFAULT (no al valor rechazado) y grita por consola, para que un
 * despliegue mal configurado se note al arrancar en vez de descubrirse en la
 * factura. `console.error` y no `LLog`: este módulo no importa nada, y todo lo
 * demás lo importa a él.
 */
export function cutoverWithProductionFloor(
  resolved: string,
  floor: string,
  isProduction: boolean = IS_PRODUCTION,
): string {
  if (!isProduction) return resolved;
  if (Date.parse(resolved) >= Date.parse(floor)) return resolved;

  console.error(
    '[envs] VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL rechazado en producción: ' +
      `${resolved} es anterior al corte publicado (${floor}). ` +
      'Adelantar el corte cobra antes de tiempo; se usa el corte publicado.',
  );

  return floor;
}

export const VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL: string =
  cutoverWithProductionFloor(
    envDate(
      import.meta.env?.VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL,
      UTILITY_WINDOW_CUTOVER_DEFAULT,
    ),
    UTILITY_WINDOW_CUTOVER_DEFAULT,
  );

// Retention booking pages: a HubSpot meetings page or a Calendly event. The
// names predate HubSpot.
export const VITE_CALENDLY_TECH_URL = import.meta.env?.VITE_CALENDLY_TECH_URL;
export const VITE_CALENDLY_VALUE_URL = import.meta.env?.VITE_CALENDLY_VALUE_URL;

// PostHog Analytics
export const VITE_POSTHOG_KEY = import.meta.env?.VITE_POSTHOG_KEY;
export const VITE_POSTHOG_HOST: string =
  import.meta.env?.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

// Base URL
export const VITE_BASE_URL_FRONTEND = import.meta.env?.VITE_BASE_URL_FRONTEND ?? process.env?.VITE_BASE_URL_FRONTEND ?? 'https://localhost:3000';

// Onboarding
export const VITE_POST_PAYMENT_ONBOARDING_ENABLED: boolean =
  import.meta.env?.VITE_POST_PAYMENT_ONBOARDING_ENABLED === 'true';
// RET-1387 dependency: minimal onboarding flow — hides the Lead Agent config
// (SMART_LEADBOT) and Team Invite steps so WhatsAppConnect becomes the terminal
// step and completion redirects straight to /pipe (where lead-profiling runs).
// Kept behind a build-time flag so prod stays on the full flow until the
// profiling pipeline (RET-1387) works end-to-end; enable on staging to validate.
export const VITE_ONBOARDING_MINIMAL: boolean =
  import.meta.env?.VITE_ONBOARDING_MINIMAL === 'true';
export const VITE_ONBOARDING_LIVE_SESSION_URL: string =
  import.meta.env?.VITE_ONBOARDING_LIVE_SESSION_URL ?? 'https://meet.google.com/gzh-kdbd-nnw';
export const VITE_ONBOARDING_SCHEDULE_URL: string =
  import.meta.env?.VITE_ONBOARDING_SCHEDULE_URL ?? 'https://lp.leadsales.io/integracion-ob';

/**
 * Búsqueda de mensajes (SRE-3328) — la sección "Mensajes" del buscador que
 * está arriba de la lista de chats del inbox.
 *
 * FAIL-CLOSED como el resto de las banderas de este archivo: cualquier valor
 * que no sea exactamente 'true' deja el buscador como la búsqueda de leads de
 * siempre, así que un typo en un despliegue no puede encenderla.
 *
 * Es build-time a propósito y no un flag de runtime: los preview environments
 * hornean las `VITE_*` en el build, así que un PR la enciende para sí mismo
 * desde su propio config de Doppler (`stg_pr_<N>`) sin tocar a nadie más. El
 * endpoint detrás es caro de estrenar a ciegas — sin ventana `since` la query
 * lee 1.29 GiB por llamada — y esta bandera es el interruptor para apagarlo
 * sin un deploy de reversa.
 *
 * Ya no basta sola: es el kill switch GLOBAL. El rollout por workspace es la
 * bandera guardada `chat_message-search` (workspace_feature_flags), y la
 * búsqueda solo aparece con ambas encendidas (useMessageSearchEnabled). No la
 * leas directo en una superficie.
 */
export const VITE_MESSAGE_SEARCH_FEATURE =
  import.meta.env?.VITE_MESSAGE_SEARCH_FEATURE === 'true';

export const VITE_LEAD_AGENT_CONFIG_V2: tLeadAgentDashboardAudience =
  parseLeadAgentDashboardAudience(import.meta.env?.VITE_LEAD_AGENT_CONFIG_V2);

// AGI-791: native assistant is off until the shared backend and cohort are released.
export const VITE_SALES_ASSISTANT_ENABLED = import.meta.env?.VITE_SALES_ASSISTANT_ENABLED === 'true';
export const VITE_WORKSPACE_AGENT_ENABLED = import.meta.env?.VITE_WORKSPACE_AGENT_ENABLED === 'true';
